import React, { useEffect, useState } from "react";
import { Container, Grid, useMediaQuery, useTheme } from "@mui/material";
import HeaderCard from "./components/DashboardHome/HeaderCard";
import LineChartCard from "./components/DashboardHome/LineChartCard";
import BarChartCard from "./components/DashboardHome/BarChartCard";
import WarehouseStockPieChart from "./components/DashboardHome/WarehouseStockPieChart";
import ValueOfStock from "./components/DashboardHome/ValueOfStock";
import TopCustomersPieChart from "./components/DashboardHome/TopCustomersPieChart";
import StockLevelsTable from "./components/DashboardHome/StockLevelsTable";
import Footer from "../Footer/Footer";
import {
  getInventorySummary,
  getDashboardInvoiceStats,
  getUserTypeCounts,
} from "../Services/DashboardService";
import PopupContainer from "./components/DashboardHome/PopupContainer";
import { getSession } from "../utils/Session";
import Swal from "sweetalert2";

// ── Format large rupee amounts compactly on small screens (₹1.2L vs
// ₹1,23,456) so the card doesn't overflow/wrap awkwardly on mobile. ────────
const fmtSalesValue = (n: number, compact: boolean) => {
  if (compact && n >= 100000) {
    return `₹${(n / 100000).toFixed(1)}L`;
  }
  if (compact && n >= 1000) {
    return `₹${(n / 1000).toFixed(1)}K`;
  }
  return `₹${n.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
};

const DashboardHome: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [inventoryData, setInventoryData] = useState({
    totalProducts: 0,
    lowStock: [],
    outOfStock: [],
    topWarehouseStock: [],
    totalInventoryValue: 0,
  });

  const [openLowStockPopup, setOpenLowStockPopup] = useState(false);
  const [openOutOfStockPopup, setOpenOutOfStockPopup] = useState(false);

  const [lowStockItems, setLowStockItems] = useState<{ itemId: string; name: string; quantity: number | string }[]>([]);
  const [outOfStockItems, setOutOfStockItems] = useState<{ itemId: string; name: string; quantity: number | string }[]>([]);

  // ── NEW: dashboard summary numbers ───────────────────────────────────────
  const [totalInvoices, setTotalInvoices] = useState<number>(0);
  const [totalSales, setTotalSales] = useState<number>(0);
  const [totalCustomers, setTotalCustomers] = useState<number>(0);
  const [totalVendors, setTotalVendors] = useState<number>(0);
  const [outstandingDue, setOutstandingDue] = useState<number>(0);
  const [pendingInvoices,setPendingInvoices]= useState<number>(0);
  

  const lowStockPopupTitle = "Low Stock Items";
  const outOfStockPopupTitle = "Out of Stock Items";

  const orgId = localStorage.getItem("orgId") ?? "";

  React.useEffect(() => {
    const session = getSession();
    if (session?.warning) {
      Swal.fire({
        icon: "warning",
        title: "License Expiry Warning",
        text: session.warning,
        confirmButtonText: "OK",
      });
    }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      const data = await getInventorySummary(orgId);
      if (data?.success) {
        setInventoryData({
          totalProducts: data.totalProducts,
          lowStock: data.lowStock || [],
          outOfStock: data.outOfStock || [],
          topWarehouseStock: data.topWarehouseStock || [],
          totalInventoryValue: data.totalInventoryValue || 0,
        });

        setLowStockItems(data.lowStock || []);
        setOutOfStockItems(data.outOfStock || []);
      }
    };
    fetchData();
  }, [orgId]);

  // ── NEW: fetch invoice stats + customer/vendor counts ───────────────────
  useEffect(() => {
    if (!orgId) return;

    const fetchInvoiceStats = async () => {
      const data = await getDashboardInvoiceStats(orgId);
      if (data?.success) {
        setTotalInvoices(data.totalInvoices || 0);
        setTotalSales(data.totalSales || 0);
        setOutstandingDue(data.outstandingDue || 0);
        setPendingInvoices(data.pendingInvoices || 0);
      }
    };

    const fetchUserCounts = async () => {
      const data = await getUserTypeCounts(orgId, ["customer", "vendor"]);
      if (data?.success) {
        setTotalCustomers(data.counts?.customer || 0);
        setTotalVendors(data.counts?.vendor || 0);
      }
    };

    fetchInvoiceStats();
    fetchUserCounts();
  }, [orgId]);

  return (
    <>
      <Container maxWidth="lg" sx={{ mt: { xs: 2, sm: 2.5 }, px: { xs: 1, sm: 2, md: 3 } }}>
        {/* ── Summary cards: 2-per-row on mobile, 3 on tablet, 4 on desktop —
            matches the compact grid convention most billing apps use on
            small screens instead of stacking every card full-width. ──────── */}
        <Grid container spacing={{ xs: 1, sm: 2 }}>
          <Grid item xs={6} sm={4} md={3}>
            <HeaderCard
              title="Total Products"
              value={inventoryData.totalProducts.toString()}
              percentage="Just updated"
              icon="Work"
            />
          </Grid>

          <Grid item xs={6} sm={4} md={3}>
            <HeaderCard
              title="Low Stock"
              value={lowStockItems.length.toString()}
              percentage="Just updated"
              icon="BarChart"
              onClick={() => setOpenLowStockPopup(true)}
            />
          </Grid>
          <PopupContainer open={openLowStockPopup} onClose={() => setOpenLowStockPopup(false)} items={lowStockItems} title={lowStockPopupTitle} />

          <Grid item xs={6} sm={4} md={3}>
            <HeaderCard
              title="Out Of Stock"
              value={outOfStockItems.length.toString()}
              percentage="Just updated"
              icon="Warning"
              onClick={() => setOpenOutOfStockPopup(true)}
            />
          </Grid>
          <PopupContainer open={openOutOfStockPopup} onClose={() => setOpenOutOfStockPopup(false)} items={outOfStockItems} title={outOfStockPopupTitle} />

          {/* ── NEW cards ──────────────────────────────────────────────── */}
          <Grid item xs={6} sm={4} md={3}>
            <HeaderCard
              title="Total Invoices"
              value={totalInvoices.toString()}
              percentage="Just updated"
              icon="Receipt"
            />
          </Grid>

          <Grid item xs={6} sm={4} md={3}>
            <HeaderCard
              title="Total Customers"
              value={totalCustomers.toString()}
              percentage="Just updated"
              icon="People"
            />
          </Grid>

                <Grid item xs={6} sm={4} md={3}>
            <HeaderCard
              title="Total Sales"
              value={totalSales.toString()}
              percentage="Just updated"
              icon="MonetizationOn"
              prefix="₹"
            />
          </Grid>
            <Grid item xs={6} sm={4} md={3}>
            <HeaderCard
              title="Outstanding Due"
              value={outstandingDue.toString()}
              percentage ={`${pendingInvoices} invoices pending`}
              icon="MonetizationOn"
              prefix="₹"
            />
          </Grid>

          {/* Replaces the old hardcoded "Suppliers" / value="0" card with
              real vendor data. */}
          <Grid item xs={6} sm={4} md={3}>
            <HeaderCard
              title="Total Vendors"
              value={totalVendors.toString()}
              percentage="Just updated"
              icon="Store"
            />
          </Grid>
        </Grid>

        {/* Chart Section */}
        <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ mt: { xs: 1, sm: 2 } }}>
          <Grid item xs={12} sm={6} md={6}>
            <BarChartCard title="Sales Performance" orgId={orgId} />
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <LineChartCard orgId={orgId} />
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <WarehouseStockPieChart stockData={inventoryData.topWarehouseStock} />
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <TopCustomersPieChart orgId={orgId} />
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <ValueOfStock totalItemValue={inventoryData.totalInventoryValue} />
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <StockLevelsTable orgId={orgId} />
          </Grid>
        </Grid>
      </Container>

      <div style={{ marginTop: isMobile ? "60px" : "100px" }}>
        <Footer />
      </div>
    </>
  );
};

export default DashboardHome;
//commented on 22/09/2026
// import React, { useEffect, useState } from "react";
// import { Container, Grid } from "@mui/material";
// import HeaderCard from "./components/DashboardHome/HeaderCard";
// import LineChartCard from "./components/DashboardHome/LineChartCard";
// import BarChartCard from "./components/DashboardHome/BarChartCard";
// import WarehouseStockPieChart from "./components/DashboardHome/WarehouseStockPieChart";
// import ValueOfStock from "./components/DashboardHome/ValueOfStock";
// import TopCustomersPieChart from "./components/DashboardHome/TopCustomersPieChart";
// import StockLevelsTable from "./components/DashboardHome/StockLevelsTable";
// import Footer from "../Footer/Footer";
// import { getInventorySummary } from "../Services/DashboardService";
// import PopupContainer from "./components/DashboardHome/PopupContainer";
// import { getSession } from "../utils/Session";
// import Swal from "sweetalert2";

// const DashboardHome: React.FC = () => {
//   const [inventoryData, setInventoryData] = useState({
//     totalProducts: 0,
//     lowStock: [],
//     outOfStock: [],
//     topWarehouseStock: [],
//     totalInventoryValue: 0,
//   });

//   const [openLowStockPopup, setOpenLowStockPopup] = useState(false);
//   const [openOutOfStockPopup, setOpenOutOfStockPopup] = useState(false);
  
//   const [lowStockItems, setLowStockItems] = useState<{ itemId: string; name: string; quantity: number | string}[]>([]);
//   const [outOfStockItems, setOutOfStockItems] = useState<{ itemId: string; name: string; quantity: number | string }[]>([]);

//   const lowStockPopupTitle = "Low Stock Items";
//   const outOfStockPopupTitle = "Out of Stock Items";

//   const orgId = localStorage.getItem("orgId") ?? "";
//   React.useEffect(() => {
//   const session = getSession();

//   if(session?.warning) {
//     Swal.fire({
//       icon: "warning",
//       title: "License Expiry Warning",
//       text: session.warning,
//       confirmButtonText: "OK",
//     });
//   }
// }, []);
//   useEffect(() => {
//     const fetchData = async () => {
//       const data = await getInventorySummary(orgId);
//       if (data?.success) {
//         setInventoryData({
//           totalProducts: data.totalProducts,
//           lowStock: data.lowStock || [],
//           outOfStock: data.outOfStock || [],
//           topWarehouseStock: data.topWarehouseStock || [],
//           totalInventoryValue: data.totalInventoryValue || 0,
//         });

//         setLowStockItems(data.lowStock || []); // Update low stock items
//         //setLowStockItems(data.lowStock.map((item: { id: any; qty: any; }) => ({ ...item, itemId: item.id, quantity: item.qty })));
//         setOutOfStockItems(data.outOfStock || []); // Update out of stock items
//       }
//     };
//     fetchData();
//   }, [orgId]);

//   return (
//     <>
//       <Container maxWidth="lg" style={{ marginTop: "20px" }}>
//         <Grid container spacing={2}>
//           <Grid item xs={12} sm={6} md={3}>
//             <HeaderCard 
//               title="Total Products" 
//               value={inventoryData.totalProducts.toString()} 
//               percentage="Just updated" 
//               icon="Work" 
//             />
//           </Grid>

//           {/* Low Stock Header with Clickable Popup */}
//           <Grid item xs={12} sm={6} md={3}>
//             <HeaderCard 
//               title="Low Stock" 
//               value={lowStockItems.length.toString()} 
//               percentage="Just updated" 
//               icon="BarChart"
//               onClick={() => setOpenLowStockPopup(true)} 
//             />
//           </Grid>

//           {/* Low Stock Popup */}
//           {/* <PopupContainer open={openLowStockPopup} onClose={() => setOpenLowStockPopup(false)} items={lowStockItems} /> */}
//           <PopupContainer open={openLowStockPopup} onClose={() => setOpenLowStockPopup(false)} items={lowStockItems} title={lowStockPopupTitle} />

//           {/* Out of Stock Header with Clickable Popup */}
//           <Grid item xs={12} sm={6} md={3}>
//             <HeaderCard 
//               title="Out Of Stock" 
//               value={outOfStockItems.length.toString()} 
//               percentage="Just updated" 
//               icon="Warning"
//               onClick={() => setOpenOutOfStockPopup(true)} 
//             />
//           </Grid>

//           {/* Out of Stock Popup */}
//           <PopupContainer open={openOutOfStockPopup} onClose={() => setOpenOutOfStockPopup(false)} items={outOfStockItems} title={outOfStockPopupTitle}/>

//           <Grid item xs={12} sm={6} md={3}>
//             <HeaderCard title="Suppliers" value="0" percentage="Just updated" icon="Store" />
//           </Grid>
//         </Grid>

//         {/* Chart Section */}
//         <Grid container spacing={2} style={{ marginTop: "20px" }}>
//           <Grid item xs={12} sm={6} md={6}>
//             <BarChartCard title="Sales Performance" orgId={orgId} />
//           </Grid>
//           <Grid item xs={12} sm={6} md={6}>
//             <LineChartCard orgId={orgId} />
//           </Grid>
//           <Grid item xs={12} sm={6} md={6}>
//             <WarehouseStockPieChart stockData={inventoryData.topWarehouseStock} />
//           </Grid>
//           <Grid item xs={12} sm={6} md={6}>
//             <TopCustomersPieChart orgId={orgId} />
//           </Grid>
//           <Grid item xs={12} sm={6} md={6}>
//             <ValueOfStock totalItemValue={inventoryData.totalInventoryValue} />
//           </Grid>
//           <Grid item xs={12} sm={6} md={6}>
//             <StockLevelsTable orgId={orgId} />
//           </Grid>
//         </Grid>
//       </Container>

//       <div style={{ marginTop: "100px" }}>
//         <Footer />
//       </div>
//     </>
//   );
// };

// export default DashboardHome;

